import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import { X, GripVertical, RotateCcw, FileText, File, Shield, Archive, Lightbulb, SwatchBook, FileCheck, FileText as LetterIcon, Plus, Settings, Calculator, Flag, FlagTriangleRight, Check, ChevronDown } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ModalPortal from './ModalPortal';
import { apiFetch } from '../utils/apiUtils';

// Global styles for animations
const globalStyles = css`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: calc(100vw - 80px);
  max-width: none;
  height: calc(100vh - 80px);
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalSubtitle = styled.div`
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 0;
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
`;

const InstructionsPanel = styled.div`
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  border-radius: 0;
  padding: 2rem 1.5rem;
  height: 100%;
`;

const InstructionsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InstructionsList = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
`;

const InstructionItem = styled.li`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MainContent = styled.div`
  min-width: 0;
  padding: 2rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionDescription = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
`;

const DefaultOrderButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  color: #6b7280;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const DocumentPriorityContainer = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const DocumentTypeManagerSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const DocumentTypeManagerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DocumentTypeManagerTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  color: ${props => props.active ? '#ffffff' : '#374151'};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
  
  &.header-tabs {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.active ? '#2563eb' : '#6b7280'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid ${props => props.active ? '#2563eb' : 'transparent'};

  &:hover {
    color: #2563eb;
    background: rgba(37, 99, 235, 0.04);
  }

  &:focus {
    outline: none;
  }
`;

const DocumentTypeForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 120px auto;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1rem;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const AddButton = styled.button`
  background: #374151;
  border: 1px solid #374151;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1f2937;
    border-color: #1f2937;
  }
`;

const DocumentTypeList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const DocumentTypeCard = styled.div`
  background: ${props => props.isEditing ? '#f8fafc' : 'white'};
  border: 1px solid ${props => props.isEditing ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
`;

const DocumentTypeColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
  border: 2px solid white;
  box-shadow: 0 0 0 1px #e5e7eb;
  flex-shrink: 0;
`;

const DocumentTypeInfo = styled.div`
  flex: 1;
`;

const DocumentTypeName = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const DocumentTypeId = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const DocumentTypeStyle = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
`;

const DeleteButton = styled.button`
  background: #6b7280;
  border: 1px solid #6b7280;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ef4444;
    border-color: #ef4444;
  }
`;

const EditButton = styled.button`
  background: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }
`;

const StyleToggleButton = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const PriorityList = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  min-height: 80px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const DocumentCard = styled.div`
  background: white;
  border: 2px solid ${props => props.isDragging ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  padding: 0;
  min-width: 160px;
  height: 160px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.2s;
  box-shadow: ${props => props.isDragging ? '0 8px 25px rgba(0, 0, 0, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  transform: ${props => props.isDragging ? 'rotate(2deg)' : 'none'};
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    border-color: ${props => props.isDragging ? '#3b82f6' : '#d1d5db'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const DocumentTitleBar = styled.div`
  background: ${props => props.color || '#f3f4f6'};
  color: ${props => props.textColor || '#6b7280'};
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  height: 50px;
  min-height: 50px;
  max-height: 50px;
  border-radius: 6px 6px 0 0;
  text-align: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const DocumentCardBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  align-items: center;
  gap: 0.75rem;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 80px;
  cursor: grab;
  z-index: 1;
  
  &:active {
    cursor: grabbing;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 36px;
  height: 20px;
  background: ${props => props.enabled ? '#475569' : '#d1d5db'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 20;
  
  &:before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.enabled ? '18px' : '2px'};
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ToggleLabel = styled.div`
  font-size: 0.6rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
`;

const getPriorityBadgeStyles = (style) => {
  switch (style) {
    case 'outline':
      return `
        background: transparent;
        color: #374151;
        border: 1px solid #374151;
      `;
    default:
      return `
        background: #374151;
        color: white;
        border: none;
      `;
  }
};

const PriorityBadge = styled.div`
  ${props => getPriorityBadgeStyles(props.badgeStyle)}
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  margin: 0 auto;
  min-width: 2rem;
`;

const ArrowSeparator = styled.div`
  color: #9ca3af;
  font-size: 1.5rem;
  font-weight: bold;
`;

const MatchingHintsSection = styled.div`
  margin-top: 1.5rem;
`;

const HintsTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FieldNameSection = styled.div`
  margin-bottom: 1.5rem;
`;

const FieldNameGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FieldNameGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldNameLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const FieldNameInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FieldNameDescription = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  line-height: 1.4;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SelectInput = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #9ca3af;
  }
`;

const TypeaheadContainer = styled.div`
  position: relative;
  width: 100%;
`;

const TypeaheadInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover:not(:disabled) {
    border-color: #9ca3af;
  }
  
  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const TypeaheadDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const TypeaheadItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MatchIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
  
  ${props => props.match ? `
    background: #dcfce7;
    color: #166534;
  ` : `
    background: #fef3c7;
    color: #92400e;
  `}
`;

const CalculatorIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.75rem;
`;

const PriorityFlagIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    ${props => {
      switch (props.priority) {
        case 'Critical':
          return `color: #dc2626;`;
        case 'Conditional':
          return `color: #ef4444;`;
        case 'High Exception':
          return `color: #2563eb;`;
        default:
          return `color: #6b7280;`;
      }
    }}
  }
`;

const MaterialSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MaterialSelectButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #9ca3af;
  }
`;

const MaterialSelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const MaterialSelectOption = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s ease;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const HintExample = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
  font-style: italic;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: #6174f7;
    color: white;
    border: 1px solid #6174f7;
    
    &:hover {
      background: #4f63f5;
      border-color: #4f63f5;
      box-shadow: 0 4px 12px rgba(97, 116, 247, 0.3);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

// Helper function to get priority indicator styles
const getPriorityIndicator = (index, style) => {
  const position = index + 1;
  
  switch (style) {
    case 'ordinals':
      const ordinals = ['1st', '2nd', '3rd', '4th'];
      return ordinals[index] || `${position}th`;
    default:
      return position;
  }
};

// Sortable Document Card Component
const SortableDocumentCard = ({ document, index, onToggle, priorityStyle, badgeStyle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: document.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const priorityContent = getPriorityIndicator(index, priorityStyle || 'ordinals');

  return (
    <DocumentCard
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
      disabled={!document.enabled}
      {...attributes}
    >
      <DragHandle {...listeners} />
      <DocumentTitleBar color={document.color} textColor={document.textColor} title={document.name}>
        {truncateTitle(document.name)}
      </DocumentTitleBar>
      <DocumentCardBody>
        <ToggleContainer>
          <ToggleSwitch 
            data-cy="document-toggle"
            enabled={document.enabled}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(document.id);
            }}
          />
          <ToggleLabel>{document.enabled ? 'Enabled' : 'Disabled'}</ToggleLabel>
        </ToggleContainer>
        {document.enabled && priorityContent && badgeStyle !== 'none' && (
          <PriorityBadge badgeStyle={badgeStyle || 'outline'}>
            {priorityContent}
          </PriorityBadge>
        )}
      </DocumentCardBody>
    </DocumentCard>
  );
};

// Helper function to move array items
function arrayMove(array, from, to) {
  const newArray = [...array];
  const [removed] = newArray.splice(from, 1);
  newArray.splice(to, 0, removed);
  return newArray;
}

// Helper function to truncate text to 8 characters
function truncateTitle(title) {
  if (!title) return '';
  return title.length > 8 ? title.substring(0, 8) + '...' : title;
}

// Main Component
const DocumentMappingModal = ({ 
  isOpen = false,
  field = null,
  onClose = () => {},
  onSave = () => {},
  isSaving = false,
  isGlobalSettings = false,
  isResetToDefault = false,
  setIsResetToDefault = () => {},
  isModalInitialized = false,
  setIsModalInitialized = () => {}
}) => {
  const getDocumentColor = (name) => {
    switch (name.toLowerCase()) {
      case 'note': return '#2563eb';
      case 'proposal': return '#059669';
      case 'service agreement': return '#d97706';
      case 'additional':
      case 'other': return '#7c3aed';
      default: return '#6b7280';
    }
  };

  const getDocumentId = (name) => {
    switch (name.toLowerCase()) {
      case 'note': return 'note';
      case 'proposal': return 'proposal';
      case 'service agreement': return 'service_agreement';
      case 'additional': return 'additional';
      case 'other': return 'additional'; // Map old "other" to "additional"
      default: return name.toLowerCase().replace(/\s+/g, '_');
    }
  };

  const formatDocumentName = (name) => {
    if (!name) return '';
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const toSnakeCase = (str) => {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .trim();
  };

  const [documents, setDocuments] = useState([]);
  const [matchingHints, setMatchingHints] = useState('');
  const [systemName, setSystemName] = useState('');
  const [promptName, setPromptName] = useState('');
  
  // Clear state when modal closes, initialize when it opens
  useEffect(() => {
    if (!isOpen) {
      setSystemName('');
      setPromptName('');
    } else if (isOpen && field) {
      // Initialize with field data when modal opens
      setSystemName(field.system_name || field.displayName || field.name || '');
      setPromptName(field.prompt_name || field.displayName || field.name || '');
    }
  }, [isOpen, field]);
  const [isCalculatedField, setIsCalculatedField] = useState(false);
  const [priorityFlag, setPriorityFlag] = useState('Critical');
  const [isResetting, setIsResetting] = useState(false);
  const [systemFieldNames, setSystemFieldNames] = useState([]);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [filteredFieldNames, setFilteredFieldNames] = useState([]);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('priority'); // 'priority' or 'types'
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [newDocumentType, setNewDocumentType] = useState({ display_name: '', color: '#6b7280', document_style: 'form' });
  const [editingType, setEditingType] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDocumentType, setEditingDocumentType] = useState(null);
  const [editFormData, setEditFormData] = useState({ display_name: '', color: '#6b7280', document_style: 'form' });
  const [defaultPriorityOrder, setDefaultPriorityOrder] = useState([]);
  const editFormRef = useRef(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load system field names for typeahead
  const loadSystemFieldNames = async () => {
    try {
      const response = await apiFetch('/system-files/system-field-names');
      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setSystemFieldNames(data.field_names || []);
      }
    } catch (error) {
      console.error('Error loading system field names:', error);
    }
  };

  // Initialize modal with field data - called explicitly when opening modal
  const initializeModal = async () => {
    try {
      setIsLoading(true);
      
      // Preserve isResetToDefault flag if it was set by resetToDefault
      if (isResetToDefault) {
        return; // Don't reset the flag, just return early
      }
        
        // Load default document priority from API with full document type details
        const response = await apiFetch('/system-files/default-document-priority');
        // apiFetch already returns parsed JSON
        const data = response;
        let loadedPriorityOrder = ['Note', 'Proposal', 'Service Agreement', 'Additional']; // fallback
        let documentDetails = [];
        
        if (data.success && data.priority_order) {
          loadedPriorityOrder = data.priority_order;
          documentDetails = data.document_details || [];
        }
        
        // Store default priority order in state for comparison in handleSave
        setDefaultPriorityOrder(loadedPriorityOrder);
        
        // Create documents array from API data or fallback to hardcoded
        const defaultDocuments = loadedPriorityOrder.map((name, index) => {
          const detail = documentDetails.find(d => d.name === name);
          return {
            id: detail?.name || getDocumentId(name),
            name: detail?.display_name || formatDocumentName(name),
            color: detail?.color || getDocumentColor(name),
            textColor: '#ffffff',
            enabled: detail?.enabled !== false
          };
        });
        
        // Add any document types that exist in the system but aren't in the priority order
        const existingDocumentIds = defaultDocuments.map(doc => doc.id);
        const missingDocumentTypes = documentDetails.filter(detail => 
          !existingDocumentIds.includes(detail.name)
        );
        
        // Add missing document types to the end of the list
        missingDocumentTypes.forEach(detail => {
          defaultDocuments.push({
            id: detail.name,
            name: detail.display_name,
            color: detail.color,
            textColor: '#ffffff',
            enabled: detail.enabled !== false
          });
        });
        
        // If this is for a specific field, load its custom settings
        // Check if field has either document_priority or enabled_documents configured
        const hasCustomSettings = field && (
          (field.document_priority && field.document_priority.length > 0) ||
          (field.enabled_documents && Object.keys(field.enabled_documents).length > 0)
        );
        
        if (hasCustomSettings) {
          const fieldDocuments = field.document_priority.map(id => {
            const defaultDoc = defaultDocuments.find(doc => doc.id === id);
            
            // Calculate enabled status
            const enabledStatus = field.enabled_documents ? field.enabled_documents[id] !== false : true;
            
            return defaultDoc ? {
              ...defaultDoc,
              enabled: enabledStatus
            } : {
              id: id,
              name: formatDocumentName(id),
              color: getDocumentColor(id),
              textColor: '#ffffff',
              enabled: enabledStatus
            };
          });
          
          // Add any document types that exist in the system but aren't in the field's priority order
          const fieldDocumentIds = fieldDocuments.map(doc => doc.id);
          const missingFieldDocumentTypes = defaultDocuments.filter(doc => 
            !fieldDocumentIds.includes(doc.id)
          );
          
          // Add missing document types to the end of the field's list
          missingFieldDocumentTypes.forEach(doc => {
            const enabledStatus = field.enabled_documents ? field.enabled_documents[doc.id] !== false : true;
            fieldDocuments.push({
              ...doc,
              enabled: enabledStatus
            });
          });
          
          setDocuments(fieldDocuments);
        } else {
          // Use default order
          setDocuments(defaultDocuments);
        }
        
        // Load matching hints if available
        // if (field && field.matching_hints) {
        //   setMatchingHints(field.matching_hints);
        // }
        
        // Load system name and prompt name
        if (field) {
          setSystemName(field.system_name || field.displayName || field.name || '');
          setPromptName(field.prompt_name || field.displayName || field.name || '');
          setIsCalculatedField(field.is_calculated_field || false);
          setPriorityFlag(field.priority_flag || 'Critical');
          
          // Don't reset isResetToDefault here - preserve it if it was set by resetToDefault
        }
        
    } catch (error) {
      console.error('Error loading default document priority:', error);
      // Fallback to hardcoded defaults
      const fallbackDocuments = [
        { id: 'note', name: 'Note', color: '#2563eb', textColor: '#ffffff', enabled: true },
        { id: 'proposal', name: 'Proposal', color: '#059669', textColor: '#ffffff', enabled: true },
        { id: 'service_agreement', name: 'Service Agreement', color: '#d97706', textColor: '#ffffff', enabled: true },
        { id: 'additional', name: 'Additional', color: '#7c3aed', textColor: '#ffffff', enabled: true }
      ];
      setDocuments(fallbackDocuments);
    } finally {
      setIsLoading(false);
    }
    
    // Also load system field names
    await loadSystemFieldNames();
    
    // Load available document types for management (only for global settings)
    if (isGlobalSettings && currentView === 'types') {
      await loadAvailableDocumentTypes();
    }
  };

  // NO useEffect - we'll initialize in the parent when opening
  
  // Load document types when switching to types view
  useEffect(() => {
    if (isOpen && isGlobalSettings && currentView === 'types') {
      loadAvailableDocumentTypes();
    }
  }, [currentView, isOpen, isGlobalSettings]);

  // Click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingDocumentType && editFormRef.current && !editFormRef.current.contains(event.target)) {
        cancelEdit();
      }
    };

    if (editingDocumentType) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingDocumentType]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setDocuments((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setIsResetToDefault(false); // User made changes, no longer reset to default
    }
  };

  const resetToDefault = () => {
    console.log('🔍 RESET TO DEFAULT FUNCTION CALLED');
    try {
      // Set flag to prevent toggleDocument from resetting isResetToDefault
      setIsResetting(true);
      
      // For field-specific settings, reset to default means CLEAR the custom settings
      // This will make the field use the global default document priority
      
      // Just set the flag immediately - no async operations needed
      setIsResetToDefault(true); // Mark that we've reset to default
      console.log('🔍 RESET TO DEFAULT: Flag set to true');
      
      // Clear the resetting flag after a brief delay
      setTimeout(() => setIsResetting(false), 100);
      
    } catch (error) {
      console.error('Error resetting to default:', error);
      setIsResetting(false);
    }
  };

  // Load available document types for management
  const loadAvailableDocumentTypes = async () => {
    try {
      const response = await apiFetch('/system-files/document-types');
      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setAvailableDocumentTypes(data.document_types);
      }
    } catch (error) {
      console.error('Error loading document types:', error);
    }
  };

  // Create new document type
  const createDocumentType = async () => {
    try {
      if (!newDocumentType.display_name) {
        alert('Please enter a display name');
        return;
      }

      const documentTypeData = {
        name: toSnakeCase(newDocumentType.display_name),
        display_name: newDocumentType.display_name,
        color: newDocumentType.color,
        document_style: newDocumentType.document_style
      };

      const response = await apiFetch('/system-files/document-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(documentTypeData)
      });

      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setNewDocumentType({ display_name: '', color: '#6b7280', document_style: 'form' });
        setShowAddForm(false);
        await loadAvailableDocumentTypes();
        await resetToDefault(); // Refresh the priority order
      } else {
        alert(`Error: ${data.detail || 'Failed to create document type'}`);
      }
    } catch (error) {
      console.error('Error creating document type:', error);
      alert('Error creating document type');
    }
  };

  // Update document type style
  const updateDocumentTypeStyle = async (documentTypeId, newStyle) => {
    try {
      const response = await apiFetch(`/system-files/document-types/${documentTypeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_style: newStyle })
      });

      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        await loadAvailableDocumentTypes();
        await resetToDefault(); // Refresh the priority order
      } else {
        alert(`Error: ${data.detail || 'Failed to update document type style'}`);
      }
    } catch (error) {
      console.error('Error updating document type style:', error);
      alert('Error updating document type style');
    }
  };

  // Edit document type
  const editDocumentType = (docType) => {
    setEditingDocumentType(docType);
    setEditFormData({
      display_name: docType.display_name,
      color: docType.color,
      document_style: docType.document_style
    });
  };

  // Save edited document type
  const saveEditedDocumentType = async () => {
    try {
      if (!editFormData.display_name) {
        alert('Please enter a display name');
        return;
      }

      const response = await apiFetch(`/system-files/document-types/${editingDocumentType.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: editFormData.display_name,
          color: editFormData.color,
          document_style: editFormData.document_style
        })
      });

      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        setEditingDocumentType(null);
        await loadAvailableDocumentTypes();
        await resetToDefault(); // Refresh the priority order
      } else {
        alert(`Error: ${data.detail || 'Failed to update document type'}`);
      }
    } catch (error) {
      console.error('Error updating document type:', error);
      alert('Error updating document type');
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingDocumentType(null);
    setEditFormData({ display_name: '', color: '#6b7280', document_style: 'form' });
  };

  // Delete document type
  const deleteDocumentType = async (documentTypeId) => {
    try {
      if (!confirm('Are you sure you want to delete this document type?')) {
        return;
      }

      const response = await apiFetch(`/system-files/document-types/${documentTypeId}`, {
        method: 'DELETE'
      });

      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        await loadAvailableDocumentTypes();
        await resetToDefault(); // Refresh the priority order
      } else {
        alert(`Error: ${data.detail || 'Failed to delete document type'}`);
      }
    } catch (error) {
      console.error('Error deleting document type:', error);
      alert('Error deleting document type');
    }
  };

  const toggleDocument = (documentId) => {
    setDocuments(documents.map(doc => 
      doc.id === documentId 
        ? { ...doc, enabled: !doc.enabled }
        : doc
    ));
    
    // Only reset the flag if we're not in the middle of a reset operation
    if (!isResetting) {
      setIsResetToDefault(false); // User made changes, no longer reset to default
    }
  };

  // Typeahead functions
  const handleSystemNameChange = (value) => {
    setSystemName(value);
    
    // Only reset the flag if we're not in the middle of a reset operation
    if (!isResetting && isResetToDefault) {
      setIsResetToDefault(false);
    }
    
    if (value.length > 0) {
      const filtered = systemFieldNames.filter(name => 
        name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredFieldNames(filtered);
      setShowTypeahead(filtered.length > 0);
    } else {
      setShowTypeahead(false);
    }
  };

  const handleSystemNameFocus = () => {
    if (systemName.length > 0) {
      const filtered = systemFieldNames.filter(name => 
        name.toLowerCase().includes(systemName.toLowerCase())
      );
      setFilteredFieldNames(filtered);
      setShowTypeahead(filtered.length > 0);
    }
  };

  const handleSystemNameBlur = () => {
    // Delay hiding to allow for click events
    setTimeout(() => setShowTypeahead(false), 200);
  };

  const selectSystemFieldName = (fieldName) => {
    setSystemName(fieldName);
    setShowTypeahead(false);
    
    // Only reset the flag if we're not in the middle of a reset operation
    if (!isResetting && isResetToDefault) {
      setIsResetToDefault(false);
    }
  };

  const isSystemNameMatch = () => {
    return systemFieldNames.includes(systemName);
  };

  // Priority dropdown functions
  const handlePrioritySelect = (priority) => {
    setPriorityFlag(priority);
    setShowPriorityDropdown(false);
  };

  const handlePriorityBlur = () => {
    setTimeout(() => setShowPriorityDropdown(false), 200);
  };

  const handleSave = () => {
    console.log('🔍 SAVE BUTTON CLICKED - isResetToDefault flag:', isResetToDefault);
    
    // Build settings object for the parent
    const settings = {
      systemName: systemName.trim(),
      promptName: promptName.trim(),
      isCalculatedField: isCalculatedField,
      priorityFlag: priorityFlag,
      matchingHints: matchingHints.trim()
    };
    
    // Build change detection object
    const currentSystemName = field.system_name || field.displayName || field.name;
    const currentPromptName = field.prompt_name || field.displayName || field.name;
    
    const changeDetection = {
      systemNameChanged: systemName.trim() !== currentSystemName,
      promptNameChanged: promptName.trim() && promptName.trim() !== currentPromptName,
      calculatedFieldChanged: isCalculatedField !== (field.is_calculated_field || false),
      priorityFlagChanged: priorityFlag !== (field.priority_flag || 'Critical'),
      systemNameDifferentFromOriginal: systemName.trim() !== (field.displayName || field.name)
    };
    
    // If we're resetting to default, create an empty documents array
    let documentsToUse = documents;
    let dirtyStateKey = 'NORMAL_SAVE';
    if (isResetToDefault) {
      console.log('🔍 RESET TO DEFAULT: Creating empty documents array');
      documentsToUse = []; // Empty array means "use global defaults"
      dirtyStateKey = 'RESET_TO_DEFAULT';
      console.log('🔍 RESET TO DEFAULT: Documents to use:', documentsToUse);
    }
    
    console.log('🔍 DIRTY STATE - Documents array:', documentsToUse);
    console.log('🔍 DIRTY STATE - Is reset to default:', isResetToDefault);
    console.log('🔍 DIRTY_STATE_KEY:', dirtyStateKey);
    console.log('🔍 DIRTY_STATE_KEY - documentsToUse:', documentsToUse);
    console.log('🔍 DIRTY_STATE_KEY - documents (original):', documents);
    console.log('🔍 DIRTY_STATE_KEY - isResetToDefault:', isResetToDefault);
    console.log('🔍 DIRTY_STATE_KEY - defaultPriorityOrder:', defaultPriorityOrder);
    
    // Call the parent's save handler - parent handles all the logic
    onSave(settings, documentsToUse, changeDetection, isResetToDefault, defaultPriorityOrder);
  };

  // Initialize modal when it opens
  useEffect(() => {
    if (isOpen && !isModalInitialized) {
      initializeModal();
      setIsModalInitialized(true);
    }
  }, [isOpen, isModalInitialized]);

  if (!isOpen || !field) return null;

  return (
    <ModalPortal isOpen={isOpen}>
      <Global styles={globalStyles} />
      <ModalOverlay data-cy="field-modal">
        <ModalContainer>
        <ModalHeader style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            <div>
              <ModalTitle>
                <SwatchBook size={20} />
                {isGlobalSettings ? 'Document Settings' : field.displayName}
              </ModalTitle>
              {!isGlobalSettings && (
                <ModalSubtitle>
                  Found in {field.foundCount?.toLocaleString()} of {field.totalRecords?.toLocaleString()} records 
                  ({field.completionRate}% completion rate)
                </ModalSubtitle>
              )}
            </div>
          </div>
          
          {/* Tab Navigation - Only show for global settings */}
          {isGlobalSettings && (
            <TabContainer 
              className="header-tabs" 
              style={{ 
                position: 'absolute',
                bottom: 0,
                right: '4rem'
              }}
            >
              <Tab 
                active={currentView === 'priority'}
                onClick={() => setCurrentView('priority')}
              >
                <GripVertical size={16} />
                Document Priority
              </Tab>
              <Tab 
                active={currentView === 'types'}
                onClick={() => setCurrentView('types')}
              >
                <SwatchBook size={16} />
                Document Types
              </Tab>
            </TabContainer>
          )}
          
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <InstructionsPanel>
            {/* Field Properties Section - Only show when not in global settings mode */}
            {!isGlobalSettings && (
              <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
              <InstructionsTitle>
                <Settings size={16} />
                Field Properties
              </InstructionsTitle>
              
              {/* Calculated Field Checkbox */}
              <CheckboxContainer style={{ marginBottom: '1rem' }}>
                <Checkbox
                  data-cy="calculated-field-checkbox"
                  type="checkbox"
                  id="calculated-field"
                  checked={isCalculatedField}
                  onChange={(e) => setIsCalculatedField(e.target.checked)}
                />
                <CheckboxLabel htmlFor="calculated-field">
                  Calculated Field
                </CheckboxLabel>
              </CheckboxContainer>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem', lineHeight: '1.4' }}>
                Check this if this field is calculated from other fields rather than extracted directly from documents
              </div>
              
              {/* Priority Flag Dropdown */}
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                  Priority Flag
                </label>
                <MaterialSelectContainer>
                  <MaterialSelectButton
                    onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                    onBlur={handlePriorityBlur}
                    type="button"
                  >
                    <span>{priorityFlag}</span>
                    <ChevronDown 
                      size={16} 
                      style={{ 
                        transform: showPriorityDropdown ? 'rotate(180deg)' : 'rotate(0deg)', 
                        transition: 'transform 0.2s ease',
                        color: '#6b7280'
                      }} 
                    />
                  </MaterialSelectButton>
                  {showPriorityDropdown && (
                    <MaterialSelectDropdown>
                      <MaterialSelectOption onClick={() => handlePrioritySelect('Critical')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <PriorityFlagIcon priority="Critical">
                            <FlagTriangleRight size={16} />
                          </PriorityFlagIcon>
                          <span>Critical</span>
                          {priorityFlag === 'Critical' && <Check size={14} color="#000000" />}
                        </div>
                      </MaterialSelectOption>
                      <MaterialSelectOption onClick={() => handlePrioritySelect('Conditional')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <PriorityFlagIcon priority="Conditional">
                            <FlagTriangleRight size={16} />
                          </PriorityFlagIcon>
                          <span>Conditional</span>
                          {priorityFlag === 'Conditional' && <Check size={14} color="#000000" />}
                        </div>
                      </MaterialSelectOption>
                      <MaterialSelectOption onClick={() => handlePrioritySelect('High Exception')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <PriorityFlagIcon priority="High Exception">
                            <FlagTriangleRight size={16} />
                          </PriorityFlagIcon>
                          <span>High Exception</span>
                          {priorityFlag === 'High Exception' && <Check size={14} color="#000000" />}
                        </div>
                      </MaterialSelectOption>
                    </MaterialSelectDropdown>
                  )}
                </MaterialSelectContainer>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.4' }}>
                Used for filtering and prioritization. Critical fields are most important for loan processing.
              </div>
              </div>
            )}
            
            <InstructionsTitle>
              <Lightbulb size={16} />
              How This Works
            </InstructionsTitle>
            <InstructionsList>
              {!isGlobalSettings ? (
                <>
                  <InstructionItem>
                    These settings only affect the <strong>{field.displayName}</strong> field
                  </InstructionItem>
                  <InstructionItem>
                    Document priority determines which documents are searched first
                  </InstructionItem>
                  <InstructionItem>
                    Drag documents to reorder priority (left = highest priority)
                  </InstructionItem>
                </>
              ) : currentView === 'priority' && (
                <>
                  <InstructionItem>
                    This sets the <strong>default priority order</strong> for all fields
                  </InstructionItem>
                  <InstructionItem>
                    Individual fields can override these settings using their field-specific configuration
                  </InstructionItem>
                  <InstructionItem>
                    Drag documents to reorder priority (left = highest priority)
                  </InstructionItem>
                  <InstructionItem>
                    Documents are searched in this order when extracting field values
                  </InstructionItem>
                  <InstructionItem>
                    Changes apply to all fields that use "Default" priority settings
                  </InstructionItem>
                </>
              )}
            </InstructionsList>
            
            {/* Document Types Instructions - Only show when on types tab */}
            {isGlobalSettings && currentView === 'types' && (
              <InstructionsList>
                <InstructionItem>
                  Add new document types with friendly names (system auto-generates IDs)
                </InstructionItem>
                <InstructionItem>
                  <strong>Edit</strong> existing types to change display name, color, or processing style
                </InstructionItem>
                <InstructionItem>
                  Choose processing style: <strong>Form</strong> (structured) or <strong>Letter</strong> (text-based)
                </InstructionItem>
                <InstructionItem>
                  Click the style button to toggle between Form and Letter processing
                </InstructionItem>
                <InstructionItem>
                  Delete unused types (only if not in priority order)
                </InstructionItem>
                <InstructionItem>
                  Changes affect how documents are processed during extraction
                </InstructionItem>
              </InstructionsList>
            )}
            
          </InstructionsPanel>
          
          <MainContent>
            {/* Document Types View */}
            {isGlobalSettings && currentView === 'types' && (
              <>
                
                
                {!showAddForm ? (
                  <AddButton onClick={() => setShowAddForm(true)} style={{ marginBottom: '1rem' }}>
                    <Plus size={16} />
                    Add Document Type
                  </AddButton>
                ) : (
                  <DocumentTypeForm>
                    <FormInput
                      type="text"
                      placeholder="Friendly Name (e.g., Insurance Policy)"
                      value={newDocumentType.display_name}
                      onChange={(e) => setNewDocumentType({...newDocumentType, display_name: e.target.value})}
                    />
                    <ColorInput
                      type="color"
                      value={newDocumentType.color}
                      onChange={(e) => setNewDocumentType({...newDocumentType, color: e.target.value})}
                    />
                    <FormSelect
                      value={newDocumentType.document_style}
                      onChange={(e) => setNewDocumentType({...newDocumentType, document_style: e.target.value})}
                    >
                      <option value="form">Form Style</option>
                      <option value="letter">Letter Style</option>
                    </FormSelect>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <AddButton onClick={createDocumentType}>
                        <Plus size={16} />
                        Add Type
                      </AddButton>
                      <Button onClick={() => setShowAddForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </DocumentTypeForm>
                )}
                    
                    <DocumentTypeList>
                      {availableDocumentTypes.map((docType) => (
                        <DocumentTypeCard key={docType.id} isEditing={editingDocumentType && editingDocumentType.id === docType.id} ref={editingDocumentType && editingDocumentType.id === docType.id ? editFormRef : null}>
                          {editingDocumentType && editingDocumentType.id === docType.id ? (
                            // Edit Form - Stack vertically to fit in container (updated layout)
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                              <div style={{ flex: 1 }}>
                                <FormInput
                                  type="text"
                                  value={editFormData.display_name}
                                  onChange={(e) => setEditFormData({...editFormData, display_name: e.target.value})}
                                  placeholder="Display Name"
                                  style={{ marginBottom: '0.75rem', display: 'block', width: '100%' }}
                                />
                                <FormSelect
                                  value={editFormData.document_style}
                                  onChange={(e) => setEditFormData({...editFormData, document_style: e.target.value})}
                                  style={{ paddingRight: '2.5rem', width: '100%' }}
                                >
                                  <option value="form">Form</option>
                                  <option value="letter">Letter</option>
                                </FormSelect>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ 
                                  width: '40px', 
                                  height: '40px', 
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  padding: '2px',
                                  backgroundColor: 'white'
                                }}>
                                  <ColorInput
                                    type="color"
                                    value={editFormData.color}
                                    onChange={(e) => setEditFormData({...editFormData, color: e.target.value})}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      border: 'none', 
                                      padding: 0, 
                                      margin: 0,
                                      cursor: 'pointer',
                                      outline: 'none',
                                      borderRadius: '2px',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <Button onClick={cancelEdit} style={{ minWidth: '80px', height: '36px' }}>
                                    Cancel
                                  </Button>
                                  <EditButton onClick={saveEditedDocumentType} style={{ minWidth: '80px', height: '36px' }}>
                                    Save
                                  </EditButton>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Normal Card View
                            <>
                              <DocumentTypeColor color={docType.color} />
                              <DocumentTypeInfo>
                                <DocumentTypeName>{docType.display_name || formatDocumentName(docType.name)}</DocumentTypeName>
                                <DocumentTypeId>{docType.name}</DocumentTypeId>
                              </DocumentTypeInfo>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <StyleToggleButton 
                                  documentStyle={docType.document_style}
                                  onClick={() => updateDocumentTypeStyle(docType.id, docType.document_style === 'form' ? 'letter' : 'form')}
                                >
                                  {docType.document_style === 'form' ? <FileCheck size={12} /> : <LetterIcon size={12} />}
                                  {docType.document_style === 'form' ? 'Form' : 'Letter'}
                                </StyleToggleButton>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <EditButton onClick={() => editDocumentType(docType)}>
                                    Edit
                                  </EditButton>
                                  <DeleteButton onClick={() => deleteDocumentType(docType.id)}>
                                    Delete
                                  </DeleteButton>
                                </div>
                              </div>
                            </>
                          )}
                        </DocumentTypeCard>
                      ))}
                </DocumentTypeList>
              </>
            )}

            {/* Document Priority Order View */}
            {isGlobalSettings && currentView === 'priority' && (
              <>
            
            <SectionTitle>
              Document Priority Order
              <DefaultOrderButton data-cy="reset-to-default-button" onClick={resetToDefault}>
                <RotateCcw size={14} />
                Reset to Default
              </DefaultOrderButton>
            </SectionTitle>
            
            <SectionDescription>
              Drag to reorder documents by priority (left = highest). Toggle to enable/disable documents for this field.
            </SectionDescription>
            
            {isLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: '#6b7280',
                fontSize: '0.875rem'
              }}>
                Loading document settings...
              </div>
            ) : (
              <DocumentPriorityContainer>
                <DndContext 
                  sensors={sensors} 
                  collisionDetection={closestCenter} 
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={documents.map(doc => doc.id)} 
                    strategy={horizontalListSortingStrategy}
                  >
                    <PriorityList>
                      {documents.map((document, index) => {
                        // Calculate priority rank based on enabled documents only
                        const enabledDocumentsBefore = documents.slice(0, index).filter(doc => doc.enabled);
                        const priorityRank = document.enabled ? enabledDocumentsBefore.length : -1;
                        
                        return (
                          <React.Fragment key={document.id}>
                            <SortableDocumentCard 
                              document={document} 
                              index={priorityRank}
                              onToggle={toggleDocument}
                              priorityStyle="ordinals"
                              badgeStyle="outline"
                            />
                            {index < documents.length - 1 && <ArrowSeparator>→</ArrowSeparator>}
                          </React.Fragment>
                        );
                      })}
                    </PriorityList>
                  </SortableContext>
                </DndContext>
              </DocumentPriorityContainer>
            )}
              </>
            )}

            {!isGlobalSettings && (
              <>
                <FieldNameSection>
                  <FieldNameGrid>
                    <FieldNameGroup>
                      <FieldNameLabel>System Name</FieldNameLabel>
                      <TypeaheadContainer>
                        <TypeaheadInput
                          data-cy="system-name-input"
                          type="text"
                          value={systemName}
                          onChange={(e) => isCalculatedField ? null : handleSystemNameChange(e.target.value)}
                          onFocus={isCalculatedField ? null : handleSystemNameFocus}
                          onBlur={isCalculatedField ? null : handleSystemNameBlur}
                          placeholder={isCalculatedField ? "Calculated field - no system name needed" : "e.g., Availability Balance"}
                          disabled={isCalculatedField}
                          style={{ 
                            backgroundColor: isCalculatedField ? '#f9fafb' : 'white',
                            cursor: isCalculatedField ? 'not-allowed' : 'text'
                          }}
                        />
                        {isCalculatedField ? (
                          <CalculatorIndicator 
                            style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}
                          >
                            <Calculator size={12} />
                          </CalculatorIndicator>
                        ) : systemName && (
                          <MatchIndicator 
                            match={isSystemNameMatch()}
                            style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}
                          >
                            {isSystemNameMatch() ? '✓' : '?'}
                          </MatchIndicator>
                        )}
                        {!isCalculatedField && showTypeahead && filteredFieldNames.length > 0 && (
                          <TypeaheadDropdown>
                            {filteredFieldNames.map((fieldName) => (
                              <TypeaheadItem
                                key={fieldName}
                                data-cy="typeahead-option"
                                onClick={() => selectSystemFieldName(fieldName)}
                              >
                                <span>{fieldName}</span>
                                <MatchIndicator match={true}>✓</MatchIndicator>
                              </TypeaheadItem>
                            ))}
                          </TypeaheadDropdown>
                        )}
                      </TypeaheadContainer>
                      <FieldNameDescription>
                        Exact field name used in your core system for database matching. Green check indicates a match with existing data.
                      </FieldNameDescription>
                    </FieldNameGroup>
                    <FieldNameGroup>
                      <FieldNameLabel>Prompt Name</FieldNameLabel>
                      <FieldNameInput
                        data-cy="prompt-name-input"
                        type="text"
                        value={promptName}
                        onChange={(e) => setPromptName(e.target.value)}
                        placeholder="e.g., Available Balance Amount"
                      />
                      <FieldNameDescription>
                        Descriptive name used by AI for better extraction accuracy
                      </FieldNameDescription>
                    </FieldNameGroup>
                  </FieldNameGrid>
                </FieldNameSection>

                {/* Document Priority Order for Field-Specific Settings */}
                <SectionTitle>
                  Document Priority Order
                  <DefaultOrderButton data-cy="reset-to-default-button" onClick={resetToDefault}>
                    <RotateCcw size={14} />
                    Reset to Default
                  </DefaultOrderButton>
                </SectionTitle>
                
                <SectionDescription>
                  Drag to reorder documents by priority (left = highest). Toggle to enable/disable documents for this field.
                </SectionDescription>
                
                {isLoading ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '200px',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    Loading document settings...
                  </div>
                ) : (
                  <DocumentPriorityContainer>
                    <DndContext 
                      sensors={sensors} 
                      collisionDetection={closestCenter} 
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext 
                        items={documents.map(doc => doc.id)} 
                        strategy={horizontalListSortingStrategy}
                      >
                        <PriorityList>
                          {documents.map((document, index) => {
                            // Calculate priority rank based on enabled documents only
                            const enabledDocumentsBefore = documents.slice(0, index).filter(doc => doc.enabled);
                            const priorityRank = document.enabled ? enabledDocumentsBefore.length : -1;
                            
                            return (
                              <React.Fragment key={document.id}>
                                <SortableDocumentCard 
                                  document={document} 
                                  index={priorityRank}
                                  onToggle={toggleDocument}
                                  priorityStyle="ordinals"
                                  badgeStyle="outline"
                                />
                                {index < documents.length - 1 && <ArrowSeparator>→</ArrowSeparator>}
                              </React.Fragment>
                            );
                          })}
                        </PriorityList>
                      </SortableContext>
                    </DndContext>
                  </DocumentPriorityContainer>
                )}

                {/* <MatchingHintsSection>
                  <SectionTitle>Matching Hints</SectionTitle>
                  <HintsTextarea
                    value={matchingHints}
                    onChange={(e) => setMatchingHints(e.target.value)}
                    placeholder="Add synonyms, alternative terms, or context to help improve field detection..."
                  />
                  <HintExample>
                    Example: "loan-to-value, LTV ratio, financing ratio, advance rate"
                  </HintExample>
                </MatchingHintsSection> */}
              </>
            )}
          </MainContent>
        </ModalBody>
        
        <ModalFooter>
          <Button data-cy="cancel-settings-button" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button 
            data-cy="save-settings-button" 
            primary 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span style={{ 
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                  marginRight: '8px'
                }} />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default DocumentMappingModal;
